/**
 * Turning picked values into the string a schema generates.
 *
 * The counterpart to `estimateNameLength`: that one answers "how long could this
 * name get", this one answers "what is this record's name". They MUST agree —
 * a counter that says a name fits while the assembler produces something longer
 * is the exact class of disagreement this package exists to remove — so the
 * placement rule below is copied from the estimator deliberately and
 * `estimator-vs-mint.spec.ts` holds them together.
 *
 * Lives here rather than in either app because both need it: the grid renders a
 * preview per row, and the API stores what it assembled. Two implementations
 * would drift, and the string is permanent once someone joins reporting on it.
 */
import { DEFAULT_FIELD_DELIMITER, DEFAULT_SUBFIELD_DELIMITER, isFieldInName } from './estimate';
import { formatFieldDate, formatPartDate } from './dateMask';
import { trackingPartLength } from './limits';
/** The stand-ins at the default part lengths; a part with its own length repeats `#` or `?` that many times. */
export const SEQUENCE_PLACEHOLDER = '#####';
export const RANDOM_PLACEHOLDER = '??????';
/** The stand-in for a sequence or random part: one `#` or `?` per {@link trackingPartLength}. */
export function partPlaceholder(part) {
    return (part.kind === 'sequence' ? '#' : '?').repeat(trackingPartLength(part));
}
/**
 * The code a field emits for a chosen value.
 *
 * A blank code falls back to the DISPLAY VALUE, not to nothing. That is what the
 * mint does — `vocabulary?.get(value) || undefined` then `?? value` — and
 * Validate reports it as an error precisely because the fallback is surprising:
 * a value with no code puts its label, spaces and all, into the name.
 */
function codeFor(field, value, options) {
    /**
     * An EMPTY resolved entry means "not resolved yet", never "empty vocabulary".
     *
     * `??` alone does not fire on `[]`, so a list still loading — or one that was
     * deleted — read as a real, empty vocabulary; the lookup missed and the
     * fallback below emitted the DISPLAY LABEL. That is the failure
     * `useResolvedListValues` documents in its own comments: a raw display string
     * baked into a name that is permanent once anything joins on it.
     * `substituteResolvedValues` and the grid's option resolution both take this
     * rule already; this was the one place that did not.
     */
    const resolved = options.resolvedValues?.[field.id]?.values;
    const vocabulary = resolved && resolved.length > 0 ? resolved : field.values;
    const match = vocabulary.find((v) => v.value === value);
    return match ? match.code || match.value : value;
}
/** Every value a cell holds, as a list — one entry unless the field is multiselect. */
function cellValues(cells, fieldId) {
    const raw = cells[fieldId];
    if (raw === undefined || raw === null) {
        return [];
    }
    return Array.isArray(raw) ? raw.filter((v) => v !== '') : raw === '' ? [] : [raw];
}
/**
 * The exact string one field contributes to a name.
 *
 * An empty cell contributes NOTHING — no placeholder, no `X`. That is the mint's
 * behaviour (`chosen.get(id) ?? ''` misses the vocabulary and returns `''`) and
 * `estimator-vs-mint.spec.ts` pins it. The prototype's `X` filler produced
 * fixed-arity strings that no generated name has ever had.
 *
 * Exported for the xlsx export, which needs each cell to hold what the name is
 * actually built from: the code, a multiselect joined with the name's own
 * delimiter, a raw ISO date shaped through the field's mask. Sharing `codeFor`
 * alone was not enough — it is the innermost step, so a caller reusing only that
 * re-derives those three rules and drifts from the assembler on every one of them.
 * Callers must pass the schema's delimiters in `options`, or the join silently
 * falls back to the default.
 */
export function fieldToken(field, cells, options, source = 'code') {
    const values = cellValues(cells, field.id);
    if (values.length === 0) {
        return '';
    }
    const join = options.multiselectDelimiter ?? options.subfieldDelimiter ?? DEFAULT_SUBFIELD_DELIMITER;
    switch (field.type) {
        case 'date':
            // The picker already wrote a formatted string; re-formatting one is how a
            // date gets mangled twice. Only a raw ISO date is shaped here.
            return values.map((v) => formatDateCell(v, field.format)).join(join);
        case 'computed':
            // Assembled by its own pattern rather than picked, so a cell for it holds
            // whatever a previous assembly stored.
            return values.join(join);
        case 'linked':
            // Nothing anywhere resolves a linked field yet, so a cell for it is
            // whatever a caller supplied.
            return values.join(join);
        default:
            return values.map((v) => (source === 'value' ? v : codeFor(field, v, options))).join(join);
    }
}
/**
 * A date cell, shaped only if it is still an ISO date.
 *
 * The date picker writes through the field's mask, so a stored cell is usually
 * already display-ready and must be emitted verbatim. Re-masking `27 Aug 2026`
 * would produce nonsense.
 */
function formatDateCell(value, mask) {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!iso) {
        return value;
    }
    /*
     * Built from the parts as LOCAL midnight. `new Date('2026-01-01')` is UTC
     * midnight, and `formatFieldDate` reads local getters, so west of UTC every
     * date came back one day early — `2026-01-01` produced `20251231`, the wrong
     * YEAR. A generated name is permanent, so the same cell must not assemble
     * differently on two machines.
     *
     * `setFullYear` rather than the constructor, because `new Date(y, …)` maps
     * years 0-99 onto 1900-1999 — `0000-01-01` would assemble as `19000101`.
     *
     * And ROUND-TRIPPED, because component construction rolls over silently: a
     * cell holding `2026-13-01` (a date field renders as free text, so a typed or
     * pasted one reaches here) became `20270101`, and `2026-02-30` became
     * `20260302`. A NaN check cannot catch either — no input this regex matches
     * produces an invalid Date. Emitting the cell verbatim is what the non-ISO
     * path already does, and a visibly odd name beats a plausible wrong one.
     */
    const [, year, month, day] = iso.map(Number);
    const parsed = new Date(0);
    parsed.setFullYear(year, month - 1, day);
    const exact = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
    return exact ? formatFieldDate(parsed, mask) : value;
}
/** One part of an explicit output pattern. */
function partToken(part, byId, cells, options) {
    switch (part.kind) {
        case 'literal':
            return part.text ?? '';
        case 'date':
            // `??`, not `||`: an unset mask means `YYYY` and an EMPTY one means emit
            // nothing. `formatPartDate` draws that distinction and a date FIELD draws
            // the opposite one, which is why neither is re-derived here.
            return formatPartDate(options.now ?? new Date(0), part.format);
        case 'sequence':
            return options.sequence ?? partPlaceholder(part);
        case 'random':
            return options.random ?? partPlaceholder(part);
        case 'field': {
            const field = part.fieldId ? byId.get(part.fieldId) : undefined;
            if (!field || options.hiddenFieldIds?.has(field.id)) {
                return '';
            }
            return fieldToken(field, cells, options, part.source === 'value' ? 'value' : 'code');
        }
        default:
            return '';
    }
}
/**
 * The implicit name: every placed field, in the schema's own order.
 *
 * The placement rule is the estimator's, copied rather than shared because the
 * estimator walks fields to add up widths and this walks them to concatenate
 * tokens — the shape is the same, the payload is not. Media top-level fields,
 * then content top-level, each immediately followed by its subfields; top-level
 * joined by the field delimiter, subfields to their parent by the subfield
 * delimiter; `linked` fields that have not opted into the name dropped BEFORE
 * grouping so neither they nor their delimiters appear.
 *
 * A cluster whose every token is empty contributes nothing, delimiter included.
 * Emitting a bare separator for an unfilled row is how a half-finished record
 * ends up looking like `__US__`.
 */
function assembleImplicit(fields, cells, options) {
    const fieldDelimiter = options.fieldDelimiter || DEFAULT_FIELD_DELIMITER;
    const subfieldDelimiter = options.subfieldDelimiter || DEFAULT_SUBFIELD_DELIMITER;
    const inName = fields.filter((f) => isFieldInName(f) && !options.hiddenFieldIds?.has(f.id));
    const topLevel = [
        ...inName.filter((f) => f.group === 'media' && !f.parentId),
        ...inName.filter((f) => f.group === 'content' && !f.parentId),
    ];
    const clusters = [];
    for (const parent of topLevel) {
        const tokens = [
            fieldToken(parent, cells, options),
            ...inName
                .filter((f) => f.parentId === parent.id)
                .map((sub) => fieldToken(sub, cells, options)),
        ].filter((t) => t !== '');
        if (tokens.length > 0) {
            clusters.push(tokens.join(subfieldDelimiter));
        }
    }
    return clusters.join(fieldDelimiter);
}
/**
 * The string a record generates.
 *
 * Pass an `output` to assemble that named formula; pass `null` to assemble the
 * implicit name from the schema's placed fields. The null case is what every
 * real schema hits today, because `taxonomy_version.outputs` has no authoring UI
 * and is therefore always empty — but the formula is fully modelled and
 * validated, so this handles both rather than being rewritten when one is
 * finally authored.
 *
 * Returns `''` for a record with nothing filled in. The caller decides what an
 * empty string means: the API stores `assembledName` as NULL so the partial
 * unique index ignores it, which is what lets a grid hold many blank rows.
 *
 * A sequence or random part with no value in `options` assembles its
 * {@link partPlaceholder}, so a caller that stores the result stores the placeholder.
 */
export function assembleOutput(output, fields, cells, options = {}) {
    if (!output) {
        return assembleImplicit(fields, cells, options);
    }
    const byId = new Map(fields.map((f) => [f.id, f]));
    const delimiter = output.delimiter ?? options.fieldDelimiter ?? DEFAULT_FIELD_DELIMITER;
    // An explicit output concatenates its parts with its own delimiter, and a part
    // that resolves to nothing is dropped rather than leaving a doubled separator.
    return output.parts
        .map((part) => partToken(part, byId, cells, options))
        .filter((token) => token !== '')
        .join(delimiter);
}
/**
 * A computed field's tracking code, from its own pattern.
 *
 * CONCATENATES. A tracking pattern has no separator of its own — the parts butt
 * together, and any literal separator is itself a part — so this passes an empty
 * delimiter rather than letting the schema's field delimiter leak in and produce
 * a code that no minted value would ever match.
 *
 * With no `sequence`/`random` supplied the volatile parts come back as `#` and
 * `?` repeated to each part's length, which is the grid's dry run: it shows the SHAPE of the code a
 * record would get without spending one. Minting is the only thing that may
 * produce a real code, and it happens server-side.
 */
export function assembleTrackingCode(field, fields, cells, options = {}) {
    const parts = field.pattern ?? [];
    if (parts.length === 0) {
        return '';
    }
    /**
     * Cells normalised through {@link trackingCellValue} FIRST.
     *
     * The whole value of a dry run is that the shape it shows is the shape that
     * gets minted, and the mint reads one trimmed value per field. Previewing the
     * raw cell instead showed `" Nike #####"` for a pasted value the mint would
     * read as `Nike`, and `NKE-ADI#####` for a multiselect the mint reads as its
     * first entry only — a preview that disagrees with the permanent thing it is
     * previewing is worse than none.
     */
    const normalized = Object.fromEntries(Object.entries(cells).map(([fieldId, raw]) => [fieldId, trackingCellValue(raw)]));
    return assembleOutput({ parts, delimiter: '' }, fields, normalized, options);
}
/**
 * One cell as the single string a tracking pattern reads.
 *
 * A pattern resolves one value per field, so a multiselect contributes its first
 * entry rather than a join — joining would bake a delimiter the pattern never
 * declared into an identifier that is permanent once issued. Trimmed, because
 * the vocabulary it is looked up in is trimmed.
 *
 * Lives here so the browser's preview and the server's mint cannot drift; they
 * did, and the preview was the half that lied.
 */
export function trackingCellValue(raw) {
    if (raw === undefined || raw === null) {
        return '';
    }
    return (Array.isArray(raw) ? (raw[0] ?? '') : raw).trim();
}
